import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { login, logout } from '../store/slices/authSlice';

interface MFASetupData {
  secret: string;
  qr_code_image: string;
  backup_codes: string[];
}

const SetupMFA: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [setupData, setSetupData] = useState<MFASetupData | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');

  useEffect(() => {
    const token = localStorage.getItem('token');

    // No token at all — not logged in, send to login
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    const initSetup = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/v1/auth/mfa/setup`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401 || res.status === 404) {
          dispatch(logout());
          navigate('/login', { replace: true });
          return;
        }

        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.detail || 'Failed to initialize MFA setup');
        }

        setSetupData(await res.json());
      } catch (e: any) {
        setError(e.message || 'Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };

    initSetup();
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Enter the 6-digit code'); return; }
    setError('');
    setVerifying(true);

    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username') || '';

    try {
      const res = await fetch(`${apiUrl}/api/v1/auth/mfa/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');

      // Backend returns a full token after MFA activation — swap out the provisional one.
      const fullToken = data.access_token;
      localStorage.setItem('token', fullToken);
      dispatch(login({ token: fullToken, username }));
      navigate('/', { replace: true });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 10, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 6, mb: 4 }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Set Up Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your account requires MFA. Scan the QR code with Google Authenticator, Authy, or any TOTP app.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {setupData && (
              <>
                <Box sx={{ textAlign: 'center', my: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <img
                    src={setupData.qr_code_image}
                    alt="MFA QR Code"
                    width={220}
                    height={220}
                    style={{ display: 'block', margin: '0 auto' }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Can't scan? Enter this secret manually:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{ mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all' }}
                  >
                    {setupData.secret}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Save these backup codes — each can be used once if you lose your device:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {setupData.backup_codes.map((c) => (
                      <Chip key={c} label={c} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box component="form" onSubmit={handleVerify}>
                  <Typography variant="subtitle2" gutterBottom>
                    Enter the 6-digit code from your app to confirm setup:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      autoFocus
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                      placeholder="123456"
                      sx={{ width: 140 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={verifying || code.length !== 6}
                    >
                      {verifying ? <CircularProgress size={20} /> : 'Activate MFA'}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default SetupMFA;
